import React from 'react';
import { Search, Trash2, Eye, MessageCircle, UserCircle2, X } from 'lucide-react';
import AdminGuestDetailView from '../../pages/admin/AdminGuestDetailView';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { Badge, Card, TimelinePreview } from './AdminPanelPrimitives';
import { STATUS_PALETTE } from './adminDesignSystem';

export const AdminGuestsPanel = ({
  expandedTimelineId,
  guestDetail,
  guestDetailLoading,
  onContactUser,
  onDeleteReview,
  onDeleteUser,
  onFetchGuestDetail,
  onRemoveQr,
  onStartConversation,
  searchQuery,
  selectedGuest,
  setSearchQuery,
  setExpandedTimelineId,
  setGuestDetail,
  setSelectedGuest,
  users,
}) => {
  if (selectedGuest) {
    return (
      <AdminGuestDetailView
        detail={guestDetail}
        loading={guestDetailLoading}
        onBack={() => { setSelectedGuest(null); setGuestDetail(null); }}
        onRemoveQr={onRemoveQr}
        onDeleteReview={onDeleteReview}
        onStartConversation={onStartConversation}
        onRefresh={() => onFetchGuestDetail(selectedGuest)}
      />
    );
  }

  const filteredGuests = users.filter((entry) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return [entry.email, entry.customer_id, entry.name].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Card style={{ marginBottom: 4, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E5E7EB', borderRadius: 12, padding: '10px 14px', background: '#F6F7F9' }}>
            <Search size={16} color="#6B7280" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              data-testid="admin-guests-search-input"
              placeholder="Search guests by e-mail, Account ID or name"
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#0F1115' }}
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                data-testid="admin-guests-search-clear"
                aria-label="Clear search"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 2 }}
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
            <span style={{ color: '#0F1115', fontWeight: 800 }}>{filteredGuests.length}</span>
            {' '}of {users.length} guest{users.length === 1 ? '' : 's'}
          </span>
        </div>
      </Card>

      {filteredGuests.map((guest) => {
        const palette = STATUS_PALETTE.active;
        return (
          <Card
            key={guest.id}
            data-testid={`admin-guest-card-${guest.id}`}
            style={{
              cursor: 'pointer',
              padding: 0,
              overflow: 'hidden',
              border: '1px solid #E7E7E7',
              boxShadow: '0 4px 12px rgba(17,17,17,0.04)',
              position: 'relative',
            }}
          >
            <div onClick={() => onFetchGuestDetail(guest.id)} style={{ display: 'flex', alignItems: 'stretch' }}>
              <div aria-hidden="true" style={{ width: 5, background: palette.solid, flexShrink: 0 }} />
              <div style={{ flex: 1, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 36, height: 36, borderRadius: '50%', background: palette.soft, color: palette.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                      {(guest.name || guest.email || '?').trim().charAt(0).toUpperCase()}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 15, fontWeight: 700, margin: 0, color: '#0F1115' }}>{guest.name || guest.email}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6B7280' }}>{guest.email}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 11.5, color: '#9CA3AF' }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#374151' }}>{guest.customer_id || '—'}</span>
                        {guest.created_at ? ` · Joined ${formatEuropeanDateTime(guest.created_at)}` : ''}
                      </p>
                    </div>
                  </div>
                  <Badge text="GUEST" tone="active" />
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onFetchGuestDetail(guest.id); }}
                    data-testid={`admin-guest-open-details-${guest.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: '#0F1115', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                  >
                    <Eye size={14} /> Open details
                  </button>
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onContactUser(guest.id); }}
                    data-testid={`admin-guest-contact-user-${guest.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                  >
                    <MessageCircle size={14} /> Contact
                  </button>
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onDeleteUser(guest.id); }}
                    data-testid={`admin-guest-delete-${guest.id}`}
                    style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: `1px solid ${STATUS_PALETTE.urgent.solid}33`, background: STATUS_PALETTE.urgent.soft, color: STATUS_PALETTE.urgent.text, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>

                <TimelinePreview
                  timelineId={`guest-${guest.id}`}
                  expandedTimelineId={expandedTimelineId}
                  setExpandedTimelineId={setExpandedTimelineId}
                  items={[
                    { label: 'Guest account active', value: guest.role },
                    { label: 'Joined', value: formatEuropeanDateTime(guest.created_at) },
                    { label: 'Account ID', value: guest.customer_id || '—' },
                  ]}
                />
              </div>
            </div>
          </Card>
        );
      })}
      {!filteredGuests.length ? <Card><p style={{ margin: 0, color: '#666' }}>No guest accounts found.</p></Card> : null}
    </div>
  );
};